<script setup>
import {ref, reactive, computed} from 'vue'
import PrintReport from './PrintReport'
import moment from 'moment'
import * as CSV from 'csv-string'
import _ from 'lodash'
import Button from "@/common/Button.vue"
import api from '@/services/api'
import {useModalsStore} from '@/stores/modals'

const modalsStore = useModalsStore()

const reports = ref([
    'Feefo online sales report',
])
const error = ref('')
const formData = reactive({
    report: '',
    startDate: '',
    endDate: '',
})
const rows = ref(undefined)
const sortBy = ref('')
const sortDir = ref('asc')

const toPrint = ref(null)

const sortedRows = computed(() => _.orderBy([...(rows.value || [])], sortBy.value, sortDir.value))
const startTimestamp = computed(() => moment(formData.startDate, 'DD-MM-YYYY').unix())
const endTimestamp = computed(() => moment(formData.endDate, 'DD-MM-YYYY').unix() + 86399)

const resultMessage = computed(() => {
    if (rows.value) {
        if (rows.value.length === 0) {
            return 'No records match your search.'
        } else {
            return rows.value.length + ' records found.'
        }
    }
    return ''
})

const setSortBy = (heading) => {
    sortDir.value = sortBy.value === heading
        ? (sortDir.value === 'asc' ? 'desc' : 'asc')
        : 'asc'

    sortBy.value = heading
}

const loadRows = () => {
    switch (formData.report) {
        case 'Feefo online sales report':
            loadFeefoReport()
            break
    }
}

const loadFeefoReport = async () => {
    rows.value = []

    try {
        const [brandsRes, typesRes, ordersRes] = await Promise.all([
            api.get('api/component/m_products_manufacturers', {
                params: {fields: ['id', 'title', 'urlSlug']}
            }),
            api.get('api/component/product_types', {
                params: {fields: ['id', 'urlSlug']}
            }),
            api.get('api/component/m_basket_orders', {
                params: {
                    requestAReview: {
                        gt: startTimestamp.value,
                        lt: endTimestamp.value,
                    },
                    orderStatus: 'Completed'
                }
            })
        ])

        const brands = brandsRes?.data?.items || []
        const types = typesRes?.data?.items || []
        const orders = ordersRes?.data?.items || []

        if (!orders.length) return

        const customerIds = orders.map(a => a.customerId)

        const customersRes = await api.post('api/component/m_registrants_registrants',
            {id: customerIds},
            {headers: {'X-Http-Method-Override': 'GET'}}
        )
        const customers = customersRes?.data?.items || []

        const orderIds = orders.map(a => a.id)

        const orderItemsRes = await api.post('api/component/m_basket_orderitems',
            {orderId: orderIds},
            {headers: {'X-Http-Method-Override': 'GET'}}
        )
        const orderItems = orderItemsRes?.data?.items || []

        if (!orderItems.length) return

        const productIds = orderItems.map(a => a.itemId)

        const productsRes = await api.post('api/component/m_products_products',
            {id: productIds},
            {headers: {'X-Http-Method-Override': 'GET'}}
        )
        const products = productsRes?.data?.items || []

        rows.value = orderItems.map((item) => {
            const order = orders.find(order => order.id === item.orderId) || {}
            const customer = customers.find(customer => customer.id === order.customerId) || {}
            const product = products.find(product => product.id === item.itemId) || {}
            const brand = brands.find(brand => brand.id === product.brand) || {}
            const type = types.find(type => type.id === product.typeId) || {}

            let productLink = 'https://www.bigbrandbeds.co.uk/'
            if (brand?.urlSlug) productLink += `${brand.urlSlug}/`
            if (type?.urlSlug) productLink += `${type.urlSlug}/`
            if (product?.urlSlug) productLink += `${product.urlSlug}/`

            const timestamp = order.completedDate || order.createdDate

            return {
                'Name': customer.displayName,
                'Email': customer.email,
                'Date': moment(timestamp * 1000).format('DD/MM/YYYY'),
                'Description': item.title,
                'Merchant identifier': 'big-brand-beds',
                'Product search code': product.sku,
                'Order ref': order.id,
                'Tags': `saleschannel=web, brand=${brand?.title || ''}`,
                'Feedback date': '',
                'Customer ref': customer.id,
                'Amount': item.gross / item.quantity,
                'Currency': 'GBP',
                'Product link': productLink,
            }
        })

    } catch (error) {
        console.error('Failed to load Feefo report:', error)
    }
}

const downloadCSVData = () => {
    if (!rows.value || !rows.value.length) return

    let headings = Object.keys(rows.value[0])
    let csv = CSV.stringify(headings)

    rows.value.forEach((row) => {
        let values = Object.values(row)
        csv += CSV.stringify(values)
    })

    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = formData.report + ' ' + getDate(new Date()) + '.csv'
    anchor.click()
}

const getDate = (timestamp) => {
    return moment(timestamp).format('DD MMM YY')
}

const openPrintModal = () => {
    if (toPrint.value) {
        let html = toPrint.value.$el.innerHTML

        modalsStore.show({
            componentName: 'PrintReportModal',
            obj: {
                html: html,
            }
        })
    }
}
</script>

<template>
    <div>
        <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

        <div class="p-3 clearfix">
            <div class="float-start">
                <form class="d-flex align-items-center" @submit.prevent="loadRows">
                    <select v-model="formData.report" class="form-select me-2" name="report">
                        <option value="">Select a report&hellip;</option>
                        <option v-for="report in reports" :key="report" :value="report">{{ report }}</option>
                    </select>

                    <template v-if="formData.report">
                        <input v-model="formData.startDate" class="form-control me-2" name="start_date"
                               placeholder="dd/mm/yyyy" size="12">
                        <span class="form-text me-2">to</span>
                        <input v-model="formData.endDate" class="form-control me-2" name="end_date"
                               placeholder="dd/mm/yyyy" size="12">
                        <button class="btn btn-secondary btn-sm" @click.prevent="loadRows">Go</button>
                    </template>
                </form>
            </div>

            <div v-if="rows && rows.length" class="float-end">
                <Button @click="openPrintModal">
                    <i class="bi-printer"/>
                    <span class="visually-hidden">Print report</span>
                </Button>

                <Button @click.prevent="downloadCSVData">
                    <i class="bi-download"/>
                    <span class="visually-hidden">Download CSV</span>
                </Button>
            </div>
        </div>

        <p v-if="resultMessage" class="text-center">
            {{ resultMessage }}
        </p>

        <div v-if="rows && rows.length" class="p-2">
            <table class="table table-condensed table-striped">
                <thead>
                <tr>
                    <td v-for="(value, heading) in rows[0]" :key="'head-' + heading" @click="setSortBy(heading)">
                        <span v-if="heading === sortBy"
                              :class="{ 'bi-chevron-up': sortDir === 'asc', 'bi-chevron-down': sortDir === 'desc' }"/>
                        {{ heading }}
                    </td>
                </tr>
                </thead>

                <tbody>
                <tr v-for="(row, rowIndex) in sortedRows" :key="'row-' + rowIndex">
                    <td v-for="(value, heading) in row" :key="'cell-' + rowIndex + '-' + heading">
                        {{ value }}
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <PrintReport v-if="rows" ref="toPrint" :end-timestamp="endTimestamp" :heading="formData.report"
                     :rows="sortedRows" :start-timestamp="startTimestamp" style="display: none"/>
    </div>
</template>

<style scoped>
.ajax-spinner {
    vertical-align: bottom;
}
</style>